import { ChapterEnum, OtherCostCalculationTypeEnum, ProjectMethodologyEnum, SectionEnum } from 'gql/graphql';
import { TYPE_VARIANTS } from 'pages/Project/Common/Contract/OtherCreate/commonData';

import type { TCatalogOtherAndLimitedItem, TCatalogSectionItem } from './types';
import {
    CatalogOtherAndLimitedTab,
    type TCatalogOtherAndLimitedRow,
    type TCatalogOtherAndLimitedTabItem,
} from './types';

export const catalogPageTitle = 'Справочник прочих и лимитированных затрат';

export const catalogOtherAndLimitedTabs: TCatalogOtherAndLimitedTabItem[] = [
    { id: CatalogOtherAndLimitedTab.Others, label: 'Прочие затраты' },
    { id: CatalogOtherAndLimitedTab.Limited, label: 'Лимитированные затраты' },
];

export const calculationTypeLabels: Record<OtherCostCalculationTypeEnum, string> = {
    [OtherCostCalculationTypeEnum.Calculation]: 'Расчет',
    [OtherCostCalculationTypeEnum.Percent]: '%',
};

export const othersChapter9GroupName = 'Прочие затраты 9 Главы';
export const othersCustomerLimitsGroupName = 'Лимиты Заказчика';
export const limitedChapter17GroupName = 'Глава 1-7';
export const limitedChapter8GroupName = 'Глава 8';
export const limitedChapter9GroupName = 'Глава 9';

export const othersGroupOrder = [othersChapter9GroupName, othersCustomerLimitsGroupName];
export const limitedGroupOrder = [limitedChapter17GroupName, limitedChapter8GroupName, limitedChapter9GroupName];

export const columnLabels = {
    [CatalogOtherAndLimitedTab.Others]: {
        name: 'Наименование прочих затрат',
        typeName: 'Тип прочих',
        createButton: 'Создать прочие',
        removeHeader: 'Удаление прочих затрат',
        removeConfirmation: 'Вы уверены, что хотите удалить запись?',
        tableUniqueId: 'catalogOtherAndLimitedOthers',
    },
    [CatalogOtherAndLimitedTab.Limited]: {
        name: 'Наименование лимитированных затрат',
        typeName: 'Тип лимитированных',
        createButton: 'Создать лимитированные',
        createTransportButton: 'Создать доп.транспорт',
        removeHeader: 'Удаление лимитированных затрат',
        removeConfirmation: 'Вы уверены, что хотите удалить запись?',
        tableUniqueId: 'catalogOtherAndLimitedLimited',
    },
};

export const defaultSet = {
    sortable: true,
    resizable: true,
};

export const isGroupRow = (
    row?: TCatalogOtherAndLimitedRow,
): row is Extract<TCatalogOtherAndLimitedRow, { isGroup: true }> => Boolean(row && 'isGroup' in row && row.isGroup);

export const cellClass = ({ data }: { data?: TCatalogOtherAndLimitedRow }) => (isGroupRow(data) ? 'sub-header' : '');

export const getGroupColSpan = (tab: CatalogOtherAndLimitedTab) => {
    const span = tab === CatalogOtherAndLimitedTab.Others ? 5 : 4;

    return ({ data }: { data?: TCatalogOtherAndLimitedRow }) => (isGroupRow(data) ? span : 1);
};

export enum CatalogCostModalTab {
    Common = 'COMMON',
    Formula = 'FORMULA',
}

export const catalogCostModalTabs = [
    { id: CatalogCostModalTab.Common, label: 'Общие данные' },
    { id: CatalogCostModalTab.Formula, label: 'Формула' },
];

export const methodologyItems = [
    { id: ProjectMethodologyEnum.Uer, label: 'УЕР' },
    { id: ProjectMethodologyEnum.Bim, label: 'БИМ' },
];

export const otherSectionItems: TCatalogSectionItem[] = [
    { id: SectionEnum.Others, label: 'Глава 9' },
    { id: SectionEnum.CustomerLimits, label: 'Лимиты Заказчика' },
];

export const limitedSectionItems: TCatalogSectionItem[] = [
    { id: ChapterEnum.Chapter8, label: 'Глава 8' },
    { id: ChapterEnum.Chapter9, label: 'Глава 9' },
];

export const additionalTransportSectionItems: TCatalogSectionItem[] = [
    { id: ChapterEnum.Chapter1_7, label: 'Глава 1-7' },
];

export const formulaUnavailableText = 'Для способа расчета «Расчет» ввод формул недоступен';

export const formatCatalogCostFormula = (formula?: string | null) => {
    if (!formula) {
        return '';
    }

    return formula.replace(/\[@([^|\]]+)(?:\|[^\]]*)?]/g, '@$1');
};

export const emptyCostTypeOptionId = 'empty';

export const otherCostTypeTooltip =
    'Расширение списка типа прочих доступно в справочнике переменных для формул прочих и лимитированных затрат';

export const limitedCostTypeTooltip =
    'Расширение списка типа лимитированных доступно в справочнике переменных для формул прочих и лимитированных затрат';

export const emptyCostTypeMessage = 'Нет доступных значений';

export const getSectionItems = (tab: CatalogOtherAndLimitedTab, isAdditionalTransport?: boolean) => {
    if (tab === CatalogOtherAndLimitedTab.Others) {
        return otherSectionItems;
    }

    return isAdditionalTransport ? additionalTransportSectionItems : limitedSectionItems;
};

export const getGroupNameBySection = (tab: CatalogOtherAndLimitedTab, section: TCatalogSectionItem) => {
    if (tab === CatalogOtherAndLimitedTab.Others) {
        return section.id === SectionEnum.CustomerLimits ? othersCustomerLimitsGroupName : othersChapter9GroupName;
    }

    if (section.id === ChapterEnum.Chapter1_7) {
        return limitedChapter17GroupName;
    }

    if (section.id === ChapterEnum.Chapter8) {
        return limitedChapter8GroupName;
    }

    return limitedChapter9GroupName;
};

export const getSectionFromItem = (tab: CatalogOtherAndLimitedTab, item: TCatalogOtherAndLimitedItem) => {
    const items = getSectionItems(tab, item.isAdditionalTransport);

    return items.find((section) => section.id === item.sectionId) ?? items[0];
};

export const getCalculatingTypeFromItem = (item?: TCatalogOtherAndLimitedItem) =>
    TYPE_VARIANTS.find((type) => type.id === item?.calculatingType) ?? TYPE_VARIANTS[0];

export const getModalTitle = (tab: CatalogOtherAndLimitedTab, isCreating: boolean, isAdditionalTransport?: boolean) => {
    if (tab === CatalogOtherAndLimitedTab.Others) {
        return isCreating ? 'Создание прочих затрат' : 'Редактирование прочих затрат';
    }

    if (isAdditionalTransport) {
        return isCreating ? 'Создание дополнительной транспортировки' : 'Редактирование дополнительной транспортировки';
    }

    return isCreating ? 'Создание лимитированных затрат' : 'Редактирование лимитированных затрат';
};

export const getOthersDuplicateNameError = ({
    name,
    calculatingType,
    sectionLabel,
    methodologyLabel,
}: {
    name: string;
    calculatingType: OtherCostCalculationTypeEnum;
    sectionLabel: string;
    methodologyLabel: string;
}) =>
    `Прочие затраты ${name} с типом расчета ${calculationTypeLabels[calculatingType]} и разделом ${sectionLabel} для методологии ${methodologyLabel} уже существуют`;

export const limitedDuplicateNameError = 'Лимитированные затраты с таким наименованием уже существуют';

export const mapOtherCostToItem = (item: {
    id: number;
    name: string;
    calculatingType: OtherCostCalculationTypeEnum;
    formula?: string | null;
    methodology: ProjectMethodologyEnum;
    section?: SectionEnum | null;
    otherCostType?: { name: string; uniqueId: string } | null;
}): TCatalogOtherAndLimitedItem => {
    const sectionId = item.section ?? SectionEnum.Others;

    return {
        id: Number(item.id),
        name: item.name,
        calculatingType: item.calculatingType,
        typeName: item.otherCostType?.name ?? '',
        typeUniqueId: item.otherCostType?.uniqueId,
        formula: item.formula ?? '',
        methodology: item.methodology,
        groupName: getGroupNameBySection(CatalogOtherAndLimitedTab.Others, {
            id: sectionId,
            label: '',
        }),
        sectionId,
    };
};

export const mapLimitedCostToItem = (item: {
    id: number;
    name: string;
    formula?: string | null;
    methodology: ProjectMethodologyEnum;
    chapter?: ChapterEnum | null;
    catalogLimitedCostType?: { name: string; uniqueId: string } | null;
}): TCatalogOtherAndLimitedItem => {
    const chapter = item.chapter ?? ChapterEnum.Chapter8;

    return {
        id: Number(item.id),
        name: item.name,
        calculatingType: OtherCostCalculationTypeEnum.Percent,
        typeName: item.catalogLimitedCostType?.name ?? '',
        typeUniqueId: item.catalogLimitedCostType?.uniqueId,
        formula: item.formula ?? '',
        methodology: item.methodology,
        groupName: getGroupNameBySection(CatalogOtherAndLimitedTab.Limited, {
            id: chapter,
            label: '',
        }),
        sectionId: chapter,
        isAdditionalTransport: chapter === ChapterEnum.Chapter1_7,
    };
};
